import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Trans, useTranslation } from "react-i18next";
import { requestPersonalGoalMultiplierSpin } from "@/apps/goals/services/goalsApi";
import { useGoalsStore } from "@/apps/goals/store/goalsStore";
import { RaffleModalCloseIcon } from "@/apps/raffle/pages/RafflePage/icons";
import { useStartupParamsStore } from "@/store/startupParams";
import Button from "@/ui/Button/Button";
import Modal from "@/ui/Modal/Modal";
import {
  GoalMultiplierSpinWheel,
} from "./GoalMultiplierSpinWheel";
import cls from "./styles.module.scss";

const SPIN_DURATION_MS = 4200;
const FULL_SPINS = 6;

const formatMultiplier = (value: number) => `x${value}`;

interface GoalMultiplierCanSpinModalProps {
  isOpen: boolean;
  onClose: () => void;
  goalMultiplierValues: number[];
}

export const GoalMultiplierCanSpinModal = ({
  isOpen,
  onClose,
  goalMultiplierValues
}: GoalMultiplierCanSpinModalProps) => {
  const { t } = useTranslation();
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const setMultiplierValue = useGoalsStore((state) => state.setMultiplierValue);
  const timeoutRef = useRef<number>(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [resultMultiplier, setResultMultiplier] = useState<number | null>(null);
  const isResult = resultMultiplier != null && !spinning;
  const resultMultiplierValue = resultMultiplier ?? 1;

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (isOpen) return;

    setSpinning(false);
    setResultMultiplier(null);
    setRotation(0);
  }, [isOpen]);

  const closeModal = () => {
    if (resultMultiplier != null) {
      setMultiplierValue(resultMultiplier);
    }

    onClose();
  };

  const spin = async () => {
    if (spinning || !yandexId) return;

    setSpinning(true);
    setResultMultiplier(null);

    let multiplier: number;

    try {
      multiplier = await requestPersonalGoalMultiplierSpin(yandexId);
    } catch {
      setSpinning(false);
      return;
    }

    const sectorAngle = 360 / goalMultiplierValues.length;
    const winningSector = Math.max(
      goalMultiplierValues.findIndex((value) => value === multiplier),
      0
    );
    const sectorMidAngle = winningSector * sectorAngle + sectorAngle / 2;
    const targetRotation = (360 - sectorMidAngle) % 360;
    const currentRotation = ((rotation % 360) + 360) % 360;
    const alignmentDelta = (targetRotation - currentRotation + 360) % 360;
    const finalRotation = rotation + FULL_SPINS * 360 + alignmentDelta;

    setRotation(finalRotation);

    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(() => {
      setSpinning(false);
      setResultMultiplier(multiplier);
    }, SPIN_DURATION_MS);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      hideHeader
      wrapperClassName={cls.canSpinModalWrapper}
      wrapperClosingClassName={cls.canSpinModalWrapperClosing}
      contentClassName={cls.canSpinModalContent}
      closeAnimationMs={240}
    >
      <div className={cls.canSpinModalBody}>
        <button
          type="button"
          className={cls.canSpinClose}
          onClick={closeModal}
          aria-label={t("goals.multiplier.close")}
        >
          <RaffleModalCloseIcon />
        </button>

        <div className={cls.canSpinHeader}>
          {isResult ? (
            <>
              <h2 className={cls.canSpinTitle}>
                <Trans
                  i18nKey="goals.multiplier.resultTitle"
                  values={{
                    multiplier: formatMultiplier(resultMultiplierValue),
                  }}
                  components={{
                    accent: <span className={cls.canSpinTitleAccentPrimary} />,
                  }}
                />
              </h2>
              <p className={cls.fullscreenResultSubtitle}>
                {t("goals.multiplier.resultSubtitle")}
              </p>
            </>
          ) : (
            <h2 className={cls.canSpinTitle}>
              <Trans
                i18nKey="goals.multiplier.canSpinTitle"
                components={{
                  accent: <span className={cls.canSpinTitleAccent} />,
                }}
              />
            </h2>
          )}
        </div>

        <div
          className={cn(
            cls.canSpinWheelSlot,
            isResult && cls.canSpinWheelSlotResult
          )}
        >
          <div className={cls.spinWheelFrame}>
            <GoalMultiplierSpinWheel
              rotation={rotation}
              spinning={spinning}
              selectedMultiplier={isResult ? resultMultiplierValue : null}
              multipliers={goalMultiplierValues}
            />
          </div>
        </div>

        <Button
          className={cls.canSpinButton}
          label={
            isResult
              ? t("goals.multiplier.backToGoal")
              : t("goals.multiplier.spinWheel")
          }
          disabled={!isResult && (!yandexId || spinning)}
          loading={!isResult && spinning}
          onClick={isResult ? closeModal : spin}
        />
      </div>
    </Modal>
  );
};
